import { Injectable } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { ACTIVE_ORGANISATION_ID_KEY, FILTERTS_ } from '../constants/common-enum';
import { ActiveOrganisationService } from './activeOrganisation.service';
import { FilterStateService } from './filter-state.service';
import { UserPermissionInitializerService } from './user-permission-initializer.service';

export interface LogoutCleanupOptions {
  returnTo?: string;
  preserveKeys?: string[];
}

@Injectable({
  providedIn: 'root'
})
export class AuthLogoutService {
  constructor(
    private auth: AuthService,
    private permissionService: UserPermissionInitializerService,
    private activeOrganisationService: ActiveOrganisationService,
    private filterService: FilterStateService
  ) { }

  /**
   * Performs a complete frontend logout:
   * - resets permissions and selected organisation,
   * - clears NZMB-specific localStorage/sessionStorage,
   * - triggers Auth0 logout so the SDK clears its own cache/session cookies,
   * - redirects the browser through the configured Auth0 logout flow.
   */
  logout(options?: LogoutCleanupOptions): void {
    const preserveKeys = new Set(options?.preserveKeys ?? []);

    this.clearApplicationState();
    this.clearApplicationStorage(preserveKeys);

    this.auth.logout({
      logoutParams: {
        returnTo: options?.returnTo ?? window.location.origin,
      },
    });
  }

  private clearApplicationState(): void {
    this.permissionService.resetPermissions();
    this.activeOrganisationService.clear();
    this.filterService.clearAllFilters();
  }

  private clearApplicationStorage(preserveKeys: Set<string>): void {
    this.clearSessionStorage(preserveKeys);
    this.clearLocalStorage(preserveKeys);
  }

  private clearSessionStorage(preserveKeys: Set<string>): void {
    const keys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && !preserveKeys.has(key)) {
        keys.push(key);
      }
    }
    keys.forEach(key => sessionStorage.removeItem(key));
  }

  private clearLocalStorage(preserveKeys: Set<string>): void {
    const nzmbKeys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && this.isApplicationStorageKey(key) && !preserveKeys.has(key)) {
        nzmbKeys.push(key);
      }
    }
    nzmbKeys.forEach(key => localStorage.removeItem(key));
  }

  private isApplicationStorageKey(key: string): boolean {
    return key === ACTIVE_ORGANISATION_ID_KEY ||
      key === 'account_inactive' ||
      key.startsWith(FILTERTS_);
  }
}
